import { useMutation } from '@apollo/client';
import { AddressFile } from '@provision/entity-types';
import { useCallback } from 'react';
import { UPDATE_ADDRESS_FILE_MUTATION } from 'src/graphql/entities/address-file/address-file.operations';
import { ConditionInput } from 'src/graphql/types';
import { generateFieldModifiersMap } from 'src/graphql/utils';

export interface UpdateAddressFileMutation {
  updateAddressFile: Partial<
    Omit<AddressFile, 'partner' | 'address' | 'uploadedByUser'> & {
      __typename: string;
    }
  >[];
}

export type UpdateAddressFileMutationParams = {
  conditions: ConditionInput[];
  data: Partial<AddressFile>;
};

export const useUpdateAddressFile = () => {
  const [updateAddressFileMutation, { loading: isUpdating }] = useMutation<
    UpdateAddressFileMutation,
    UpdateAddressFileMutationParams
  >(UPDATE_ADDRESS_FILE_MUTATION, {
    update: (cache, { data }) => {
      if (!data?.updateAddressFile?.length) return;
      const updateResult = data.updateAddressFile[0];
      const id = cache.identify({
        __typename: 'AddressFile',
        id: updateResult.addressFileId,
      });

      const fields = generateFieldModifiersMap({
        fileName: updateResult.fileName ?? '',
        updatedAt: updateResult.updatedAt,
        pinnedAt: updateResult.pinnedAt,
      });

      cache.modify({
        id,
        fields,
      });
    },
  });

  const updateAddressFile = useCallback(
    async (id: string, partial: Partial<AddressFile>, addressFile: AddressFile) => {
      await updateAddressFileMutation({
        variables: {
          conditions: [
            {
              attribute: 'id',
              operator: '=',
              value: String(id),
            },
          ],
          data: {
            ...partial,
          },
        },
        optimisticResponse: {
          updateAddressFile: [
            {
              ...addressFile,
              ...partial,
              __typename: 'AddressFile',
              id,
              updatedAt: new Date().toISOString(),
            },
          ],
        },
      });
    },
    [updateAddressFileMutation],
  );

  return {
    updateAddressFile,
    isUpdating,
  };
};
